import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import debounce from 'lodash/debounce';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { RootState } from 'redux/schemas';
import {
  getRolePlayGallerySubmissionIds,
  getRolePlayGallerySubmissions,
} from 'redux/selectors/timeline';
import useAiRolePlay from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play';
import {
  getAiRolePlaySubmissionsList,
  SUBMISSIONS_LIST_FILTERS,
  SubmissionsListFilter,
} from 'redux/actions/ai-role-play';
import { AiRolePlaySubmissionsListState, FeedbackEvaluationCounts } from 'redux/schemas/app/ai-role-play';

import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import { makeQueryString, useQuery } from 'shared/hooks/use-query';
import {
  halfSpacing,
  largeSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { gray2, gray5, primary, white } from 'styles/global_defaults/colors';

import { NvSearchBar } from 'shared/components/nv-search-bar';
import TabLoadingWrapper from 'practice_room/components/shared/tab-loading-wrapper';
import RolePlaySubmissionCard from './role-play-submission-card';
import SubmissionFeedbackHeader from './submission-feedback-header';

const PAGE_SIZE = 10;

const isValidFilter = (value: unknown): value is SubmissionsListFilter => (
  typeof value === 'string'
  && (SUBMISSIONS_LIST_FILTERS as readonly string[]).includes(value)
);

const styles = css`
  .loading-placeholder {
    justify-content: center;
    margin-top: ${largeSpacing}px;
  }

  .nv-search-bar {
    margin-bottom: ${standardSpacing}px;
    padding: ${halfSpacing}px;
    border: 1px solid ${gray5};
    border-radius: ${halfSpacing}px;

    &:focus-within {
      border-color: ${primary};
    }

    input {
      width: 100%;
    }
  }

  .gallery-filters {
    gap: ${standardSpacing / 2}px;
  }

  .filter-pill {
    padding: 0 ${standardSpacing}px;
    border-radius: ${largeSpacing}px;
    border: 1px solid ${gray5};
    background-color: ${white};
    cursor: pointer;
    font-size: 14px;
    line-height: 35px;
    color: ${gray2};

    &.is-selected {
      background-color: ${primary};
      border-color: ${primary};
      color: white;
    }
  }

  .gallery-content {
    max-width: 980px;
  }

  .gallery-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    column-gap: ${largeSpacing}px;
    row-gap: ${standardSpacing}px;
  }
`;

const FILTER_COUNT_KEY: Record<SubmissionsListFilter, keyof FeedbackEvaluationCounts> = {
  all: 'all',
  excellent: 'excellent',
  good_job: 'goodJob',
  needs_work: 'needsWork',
};

type SubmissionsGalleryContentProps = {
  rolePlayId: number;
};

const SubmissionsGalleryContent = ({ rolePlayId }: SubmissionsGalleryContentProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const aiRolePlay = useAiRolePlay(rolePlayId);
  const catalogId = aiRolePlay?.courseCatalogId;

  const gallery = useSelector<RootState, AiRolePlaySubmissionsListState | undefined>(
    (state: RootState) => getRolePlayGallerySubmissions(state, rolePlayId),
  );
  const submissionIds = useSelector<RootState, number[]>(
    (state: RootState) => getRolePlayGallerySubmissionIds(state, rolePlayId),
  );

  const isOnceLoaded = !!gallery?.isOnceLoaded;
  const isLoading = !!gallery?.isLoading;
  const hasMore = !!gallery?.hasMore;

  // URL drives the filter + search; `useQuery` parses the React-Router
  // location's query string. The URL is the source of truth so the modal
  // is shareable / deep-linkable.
  const urlParams = useQuery();
  const filterBy: SubmissionsListFilter = isValidFilter(urlParams.filter)
    ? urlParams.filter
    : 'all';
  const search: string = typeof urlParams.search === 'string' ? urlParams.search : '';

  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Reset to page 1 whenever the URL-driven filter or search changes.
  useEffect(() => {
    setPage(1);
  }, [filterBy, search]);

  // Keep the input echo in sync if the URL changes from outside (back button,
  // pasted link). When the user types, this also re-runs but is a no-op since
  // the value already matches.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const pushUrlParams = useCallback(
    (nextSearch: string, nextFilter: SubmissionsListFilter) => {
      history.push(makeQueryString({
        search: nextSearch || undefined,
        filter: nextFilter !== 'all' ? nextFilter : undefined,
      }));
    },
    [history],
  );

  // Push debounced search into the URL 300ms after the user stops typing.
  // The current filter is passed at call-site so a stale closure doesn't
  // overwrite a recently-changed filter.
  const debouncedPushSearch = useMemo(
    () => debounce(pushUrlParams, 300),
    [pushUrlParams],
  );

  useEffect(() => () => debouncedPushSearch.cancel(), [debouncedPushSearch]);

  const handleFilterChange = (value: SubmissionsListFilter) => {
    if (value === filterBy) return;
    debouncedPushSearch.cancel();
    pushUrlParams(searchInput, value);
  };

  // Single fetch effect, driven by URL params + page. Page-1 fetches replace
  // the list in the reducer; page>1 fetches append.
  // The cleanup aborts the in-flight request when deps change or on unmount.
  useEffect(() => {
    if (!rolePlayId || !catalogId) return undefined;
    const request = dispatch(getAiRolePlaySubmissionsList({
      catalogId,
      aiRolePlayId: rolePlayId,
      page,
      pageSize: PAGE_SIZE,
      filterBy,
      search: search || undefined,
    }));
    return () => { request.abort(); };
  }, [page, filterBy, search, rolePlayId, catalogId, dispatch]);

  // Queried once at mount via lazy useState — the modal wrapper is an ancestor
  // so it exists before this component renders, but querying during render on
  // every re-render is a side effect. Lazy init runs the selector once only.
  const [scrollingElement] = useState<HTMLElement | null>(
    () => document.querySelector<HTMLElement>('#modal-content-wrapper'),
  );
  const isPageBottom = useInfiniteScroll(scrollingElement, 40);

  useEffect(() => {
    if (scrollingElement && isPageBottom && !isLoading && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [scrollingElement, isPageBottom, isLoading, hasMore]);

  // Bottom-of-list loader only after the first page has loaded
  useEffect(() => {
    if (isOnceLoaded && page > 1) {
      setIsPageLoading(isLoading);
    } else {
      setIsPageLoading(false);
    }
  }, [isOnceLoaded, isLoading, page]);

  return (
    <div css={styles}>
      {aiRolePlay && (
        <SubmissionFeedbackHeader
          rolePlayTitle={aiRolePlay.title}
          showUser={false}
        />
      )}
      <div className='px-5 py-4'>
        <NvSearchBar
          placeholder={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.SEARCH_PLACEHOLDER()}
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.ARIA_LABEL.SEARCH()}
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInput(e.target.value);
            debouncedPushSearch(e.target.value, filterBy);
          }}
          onSearch={(query) => {
            debouncedPushSearch.cancel();
            pushUrlParams(query, filterBy);
          }}
          clearButtonEnabled={false}
        />
        <div
          className='gallery-filters d-flex flex-wrap align-items-center mb-3'
          role='group'
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.ARIA_LABEL.FILTER()}
        >
          {SUBMISSIONS_LIST_FILTERS.map((value) => {
            const labelKey = value.toUpperCase() as Uppercase<SubmissionsListFilter>;
            return (
              <button
                key={value}
                type='button'
                className={`filter-pill ${filterBy === value ? 'is-selected' : ''}`}
                onClick={() => handleFilterChange(value)}
                aria-pressed={filterBy === value}
              >
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.FILTER[labelKey](
                  gallery?.feedbackEvaluationCounts?.[FILTER_COUNT_KEY[value]] ?? 0,
                )}
              </button>
            );
          })}
          <span className='gallery-sort-label ml-auto text-gray-2 text-medium'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.SORTED_BY_RECENT()}
          </span>
        </div>

        <TabLoadingWrapper isLoaded={isOnceLoaded} isMoreLoading={isPageLoading}>
          <div className='gallery-content mx-auto'>
            {submissionIds.length > 0 && (
              <div className='gallery-cards py-2'>
                {submissionIds.map((id: number) => (
                  <RolePlaySubmissionCard key={id} submissionId={id} />
                ))}
              </div>
            )}
            {submissionIds.length === 0 && (
              <div className='gallery-empty text-center text-gray-3 p-5'>
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SUBMISSION_LIST.NO_SUBMISSIONS()}
              </div>
            )}
            {submissionIds.length > 0 && !hasMore && !isPageLoading && (
              <div className='gallery-all-loaded py-4 text-gray-3'>{t.SEARCH.ALL_LOADED()}</div>
            )}
          </div>
        </TabLoadingWrapper>
      </div>
    </div>
  );
};

export default SubmissionsGalleryContent;
